import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function ROICalculator() {
  const navigate = useNavigate();
  const [leadsPerMonth, setLeadsPerMonth] = useState(4);
  const [conversionRate, setConversionRate] = useState(88);
  const [avgProjectValue, setAvgProjectValue] = useState(2500);
  const [costPerLead, setCostPerLead] = useState(45);

  const monthlyInvestment = leadsPerMonth * costPerLead;
  const convertedLeads = Math.round((leadsPerMonth * conversionRate) / 100);
  const monthlyRevenue = convertedLeads * avgProjectValue;
  const monthlyProfit = monthlyRevenue - monthlyInvestment;
  const roi = monthlyInvestment > 0 ? ((monthlyProfit / monthlyInvestment) * 100).toFixed(0) : '0';

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Calculez Votre Retour sur Investissement
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez combien vous pouvez gagner en achetant des leads qualifiés
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          {/* Left: Calculator Inputs */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 flex flex-col">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Vos Paramètres</h3>
            
            <div className="space-y-6 flex-1">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre de leads par mois
                </label>
                <input
                  type="range"
                  min="5"
                  max="50"
                  value={leadsPerMonth}
                  onChange={(e) => setLeadsPerMonth(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-sm text-gray-600 mt-2">
                  <span>5 leads</span>
                  <span className="font-bold text-blue-600">{leadsPerMonth} leads</span>
                  <span>50 leads</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Taux de conversion (%)
                </label>
                <input
                  type="range"
                  min="10"
                  max="80"
                  value={conversionRate}
                  onChange={(e) => setConversionRate(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                />
                <div className="flex justify-between text-sm text-gray-600 mt-2">
                  <span>10%</span>
                  <span className="font-bold text-teal-600">{conversionRate}%</span>
                  <span>80%</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Chiffre d'affaires moyen par projet (€)
                </label>
                <input
                  type="range"
                  min="500"
                  max="10000"
                  step="100"
                  value={avgProjectValue}
                  onChange={(e) => setAvgProjectValue(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-sm text-gray-600 mt-2">
                  <span>500€</span>
                  <span className="font-bold text-blue-600">{avgProjectValue.toLocaleString()}€</span>
                  <span>10 000€</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Coût par lead (€)
                </label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="5"
                  value={costPerLead}
                  onChange={(e) => setCostPerLead(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                />
                <div className="flex justify-between text-sm text-gray-600 mt-2">
                  <span>10€</span>
                  <span className="font-bold text-teal-600">{costPerLead}€</span>
                  <span>100€</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Results */}
          <div className="bg-gradient-to-br from-blue-600 to-teal-600 rounded-2xl p-8 shadow-xl text-white flex flex-col">
            <h3 className="text-2xl font-bold mb-6">Vos Résultats Mensuels</h3>
            
            <div className="space-y-6 flex-1">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-user-follow-line text-2xl"></i>
                  <span className="text-sm font-medium text-blue-100">Clients Convertis</span>
                </div>
                <div className="text-4xl font-bold">{convertedLeads}</div>
                <div className="text-sm text-blue-100 mt-1">sur {leadsPerMonth} leads achetés</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-money-euro-circle-line text-2xl"></i>
                  <span className="text-sm font-medium text-blue-100">Chiffre d'Affaires Généré</span>
                </div>
                <div className="text-4xl font-bold">{monthlyRevenue.toLocaleString()}€</div>
                <div className="text-sm text-blue-100 mt-1">revenus mensuels</div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-wallet-line text-2xl"></i>
                  <span className="text-sm font-medium text-blue-100">Investissement Total</span>
                </div>
                <div className="text-4xl font-bold">{monthlyInvestment.toLocaleString()}€</div>
                <div className="text-sm text-blue-100 mt-1">coût des leads</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-line-chart-line text-2xl text-blue-600"></i>
                  <span className="text-sm font-medium text-gray-700">Bénéfice Net</span>
                </div>
                <div className="text-4xl font-bold text-blue-600">{monthlyProfit.toLocaleString()}€</div>
                <div className="text-sm text-gray-600 mt-1">profit mensuel</div>
              </div>
            </div>

            <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">ROI (Retour sur Investissement)</span>
                <span className="text-2xl font-bold">{roi}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <button
            onClick={() => {
              navigate('/inscription-candidat');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-3 bg-gradient-to-br from-blue-600 to-teal-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-shopping-cart-line text-2xl"></i>
            Commencer à Acheter des Leads
          </button>
        </div>
      </div>
    </section>
  );
}
